import { resolveEditor, safeGetSelected } from './customCss';

/**
 * Device visibility control for the Style Manager "General" sector.
 *
 * Unlike a normal Style Manager property, this writes HTML attributes
 * (data-gjs-hide-desktop/tablet/mobile) directly on the selected component
 * instead of CSS. That keeps it device-independent and reliable — the managed
 * media-query CSS (buildDeviceVisibilityCss) hides any element whose attribute
 * is "1" on the matching breakpoint. Legacy pages used the inverted
 * data-gjs-show-*="0" scheme, which is read here and converted on first change.
 */
const DEVICES = [
  { key: 'desktop', label: 'Desktop', hideAttr: 'data-gjs-hide-desktop', showAttr: 'data-gjs-show-desktop' },
  { key: 'tablet', label: 'Tablet', hideAttr: 'data-gjs-hide-tablet', showAttr: 'data-gjs-show-tablet' },
  { key: 'mobile', label: 'Mobile', hideAttr: 'data-gjs-hide-mobile', showAttr: 'data-gjs-show-mobile' },
];

function isHidden(attrs, device) {
  if (device.hideAttr in attrs) return String(attrs[device.hideAttr]).trim() === '1';
  if (device.showAttr in attrs) return String(attrs[device.showAttr]).trim() === '0';
  return false;
}

export function registerDeviceVisibilityProperty(editor) {
  const sm = editor?.StyleManager;
  if (!sm || typeof sm.addType !== 'function') return;

  const getSelected = () => safeGetSelected(resolveEditor(editor));

  // Always (re)register so the closure points at the current editor instance.
  sm.addType('device-visibility', {
    create() {
      const wrap = document.createElement('div');
      wrap.className = 'gjs-field-device-visibility';

      const hint = document.createElement('div');
      hint.className = 'gjs-device-visibility-hint';
      hint.textContent = 'Hide the selected element on:';
      wrap.appendChild(hint);

      const row = document.createElement('div');
      row.className = 'gjs-device-visibility-row';
      wrap.appendChild(row);

      const checkboxes = {};

      DEVICES.forEach((device) => {
        const label = document.createElement('label');
        label.className = 'gjs-device-visibility-item';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('data-device', device.key);

        const text = document.createElement('span');
        text.textContent = device.label;

        input.addEventListener('change', () => {
          const component = getSelected();
          if (!component || typeof component.addAttributes !== 'function') {
            input.checked = false;
            return;
          }
          component.addAttributes({ [device.hideAttr]: input.checked ? '1' : '0' });
          // Drop the legacy attribute so exported HTML uses one scheme.
          const attrs = component.getAttributes?.() || {};
          if (device.showAttr in attrs) component.removeAttributes?.(device.showAttr);
        });

        label.appendChild(input);
        label.appendChild(text);
        row.appendChild(label);
        checkboxes[device.key] = input;
      });

      const refresh = () => {
        const component = getSelected();
        const attrs = component?.getAttributes?.() || {};
        const hasComponent = !!component;
        DEVICES.forEach((device) => {
          const input = checkboxes[device.key];
          input.disabled = !hasComponent;
          input.checked = hasComponent ? isHidden(attrs, device) : false;
        });
        wrap.classList.toggle('gjs-device-visibility--disabled', !hasComponent);
      };

      editor.on('component:selected', refresh);
      editor.on('component:deselected', refresh);
      refresh();

      wrap._deviceVisibilityRefresh = refresh;
      wrap._deviceVisibilityDestroy = () => {
        editor.off('component:selected', refresh);
        editor.off('component:deselected', refresh);
      };

      return wrap;
    },
    emit() {
      // Applied directly on checkbox change in create().
    },
    update({ el }) {
      if (el?._deviceVisibilityRefresh) el._deviceVisibilityRefresh();
    },
    destroy({ el }) {
      if (el?._deviceVisibilityDestroy) el._deviceVisibilityDestroy();
    },
  });
}
